import { io, Socket } from "socket.io-client";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

let socket: Socket | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
let reconnectTimeout: NodeJS.Timeout | null = null;

export interface NotificationData {
  notification_id: number;
  title: string;
  message: string;
  event_type: string;
  event_subtype?: string;
  category: string;
  severity: string;
  priority: string;
  action_url?: string;
  icon?: string;
  requires_action: boolean;
  created_date: string;
}

export type NotificationCallback = (notification: NotificationData) => void;
export type ConnectionCallback = (connected: boolean) => void;

const notificationCallbacks: Set<NotificationCallback> = new Set();
const connectionCallbacks: Set<ConnectionCallback> = new Set();

/**
 * Initialize WebSocket connection
 */
export const connectWebSocket = (): void => {
  if (socket?.connected) {
    console.log("✅ WebSocket already connected");
    return;
  }

  const token = sessionStorage.getItem("entra_token");
  if (!token) {
    console.warn("⚠️ No token available for WebSocket connection");
    return;
  }

  // Extract base URL without /api
  const wsUrl = API_BASE_URL.replace(/\/api$/, "");

  console.log("🔌 Connecting to WebSocket:", wsUrl);

  socket = io(wsUrl, {
    auth: {
      token: token,
    },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
  });

  socket.on("connect", () => {
    console.log("✅ WebSocket connected");
    reconnectAttempts = 0;
    notifyConnectionCallbacks(true);
  });

  socket.on("disconnect", (reason) => {
    console.log("🔌 WebSocket disconnected:", reason);
    notifyConnectionCallbacks(false);
    
    // Attempt to reconnect if not manually disconnected
    if (reason !== "io client disconnect") {
      scheduleReconnect();
    }
  });

  socket.on("connect_error", (error) => {
    console.error("❌ WebSocket connection error:", error.message);
    notifyConnectionCallbacks(false);
  });

  socket.on("connected", (data) => {
    console.log("📡 WebSocket handshake complete:", data);
  });

  socket.on("notification", (data: { type: string; data: NotificationData; timestamp: string } | NotificationData) => {
    console.log("📬 Received real-time notification - RAW:", data);
    
    // Handle both formats: wrapped { data: NotificationData } or direct NotificationData
    let notificationData: NotificationData;
    
    if ('data' in data && data.data) {
      // Wrapped format: { type: string; data: NotificationData; timestamp: string }
      notificationData = data.data;
      console.log("📬 Extracted notification data from wrapped format:", notificationData);
    } else {
      // Direct format: NotificationData
      notificationData = data as NotificationData;
      console.log("📬 Using direct notification data format:", notificationData);
    }
    
    // Log all fields to debug
    console.log("📬 Notification fields:", {
      notification_id: notificationData.notification_id,
      title: notificationData.title,
      message: notificationData.message,
      event_type: notificationData.event_type,
      event_subtype: notificationData.event_subtype,
      category: notificationData.category,
      severity: notificationData.severity,
      priority: notificationData.priority,
      created_date: notificationData.created_date,
    });
    
    if (notificationData) {
      notifyNotificationCallbacks(notificationData);
    } else {
      console.error("❌ No notification data found in received payload");
    }
  });

  socket.on("error", (error) => {
    console.error("❌ WebSocket error:", error);
  });
};

/**
 * Disconnect WebSocket
 */
export const disconnectWebSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  reconnectAttempts = 0;
  notifyConnectionCallbacks(false);
};

/**
 * Schedule reconnection attempt
 */
const scheduleReconnect = (): void => {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn("⚠️ Max reconnection attempts reached");
    return;
  }

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
  }

  reconnectAttempts++;
  const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000); // Exponential backoff, max 30s

  reconnectTimeout = setTimeout(() => {
    console.log(`🔄 Attempting to reconnect (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
    disconnectWebSocket();
    connectWebSocket();
  }, delay);
};

/**
 * Subscribe to notifications
 */
export const subscribeToNotifications = (callback: NotificationCallback): (() => void) => {
  notificationCallbacks.add(callback);
  return () => {
    notificationCallbacks.delete(callback);
  };
};

/**
 * Subscribe to connection status
 */
export const subscribeToConnection = (callback: ConnectionCallback): (() => void) => {
  connectionCallbacks.add(callback);
  return () => {
    connectionCallbacks.delete(callback);
  };
};

/**
 * Notify all notification callbacks
 */
const notifyNotificationCallbacks = (notification: NotificationData): void => {
  notificationCallbacks.forEach((callback) => {
    try {
      callback(notification);
    } catch (error) {
      console.error("Error in notification callback:", error);
    }
  });
};

/**
 * Notify all connection callbacks
 */
const notifyConnectionCallbacks = (connected: boolean): void => {
  connectionCallbacks.forEach((callback) => {
    try {
      callback(connected);
    } catch (error) {
      console.error("Error in connection callback:", error);
    }
  });
};

/**
 * Acknowledge notification
 */
export const acknowledgeNotification = (notificationId: number): void => {
  if (socket?.connected) {
    socket.emit("notification:ack", { notification_id: notificationId });
  }
};

/**
 * Check if WebSocket is connected
 */
export const isWebSocketConnected = (): boolean => {
  return socket?.connected || false;
};

/**
 * Reconnect WebSocket (useful after token refresh)
 */
export const reconnectWebSocket = (): void => {
  disconnectWebSocket();
  connectWebSocket();
};


